/**
 * Fixed, data-free assets for the interactive HTML snapshot exporter (S15):
 * the CSS + the tiny vanilla-JS interaction script (local table sort, theme
 * toggle). Both strings are CONSTANT — no tile/user data is ever
 * interpolated into them — so their SHA-256 hashes can be pinned once and
 * referenced by the page's `Content-Security-Policy` meta tag
 * (`style-src`/`script-src 'sha256-…'`), rather than falling back to
 * `'unsafe-inline'`. `htmlSnapshotAssets.test.ts` recomputes both hashes
 * from these exact strings and fails loudly if either drifts.
 *
 * `escapeHtml` mirrors `electron/dashboards/dashboardPrintDocument.ts`'s
 * function of the same name — duplicated because this module runs in the
 * renderer (no cross-process imports) and is intentionally tiny.
 */
import { GRID_COLS } from '../utils/gridLayout';

/** Row cap per tile baked into a snapshot — see plan Task S15. */
export const HTML_SNAPSHOT_ROW_CAP = 1000;

export const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const SNAPSHOT_STYLE_CSS = `:root{--bg:#ffffff;--fg:#1c1c1f;--muted:#6b6b74;--border:#e2e2e6;--card:#f7f7f8;--accent:#3b82f6;--warn-bg:#fef3c7;--warn-fg:#92400e}
html.dark{--bg:#1c1c1f;--fg:#e6e6ea;--muted:#9a9aa3;--border:#333338;--card:#26262b;--accent:#60a5fa;--warn-bg:#451a03;--warn-fg:#fbbf24}
*{box-sizing:border-box}
body{margin:0;background:var(--bg);color:var(--fg);font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;padding:20px}
header.snap-header{margin-bottom:16px;display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap}
header.snap-header h1{font-size:18px;font-weight:600;margin:0 0 4px}
.snap-meta{color:var(--muted);font-size:11px}
.snap-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px}
.snap-chip{border:1px solid var(--border);border-radius:999px;padding:2px 8px;font-size:11px;color:var(--muted);background:var(--card)}
#theme-toggle-btn{border:1px solid var(--border);background:var(--card);color:var(--fg);border-radius:6px;padding:4px 10px;font-size:12px;cursor:pointer;white-space:nowrap}
.snap-grid{display:grid;grid-template-columns:repeat(${GRID_COLS},1fr);grid-auto-rows:56px;gap:10px}
.snap-tile{border:1px solid var(--border);border-radius:8px;background:var(--card);padding:8px;overflow:hidden;display:flex;flex-direction:column;min-width:0;min-height:0}
.snap-tile h2{font-size:12px;font-weight:600;margin:0 0 6px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.snap-tile-body{flex:1;min-height:0;overflow:auto}
.snap-empty{color:var(--muted);font-size:11px;font-style:italic;padding:6px 0}
.snap-banner{background:var(--warn-bg);color:var(--warn-fg);font-size:10px;border-radius:4px;padding:3px 6px;margin-bottom:6px}
table.snap-table{width:100%;border-collapse:collapse;font-size:11px}
table.snap-table th,table.snap-table td{padding:3px 6px;border-bottom:1px solid var(--border);text-align:left;white-space:nowrap}
table.snap-table th[data-sort-idx]{cursor:pointer;user-select:none}
table.snap-table th.snap-sort-asc::after{content:' \\25B2'}
table.snap-table th.snap-sort-desc::after{content:' \\25BC'}
.snap-stat-value{font-size:26px;font-weight:700}
.snap-stat-value[data-severity="warning"]{color:#d97706}
.snap-stat-value[data-severity="critical"]{color:#dc2626}
.snap-stat-compare{font-size:11px;color:var(--muted);margin-top:4px}
.snap-note{white-space:pre-wrap;font-size:12px}
.snap-chart-svg{width:100%;height:auto}
.snap-chart-svg text{fill:var(--muted);font-size:9px}
.snap-chart-legend{display:flex;flex-wrap:wrap;gap:8px;font-size:10px;color:var(--muted);margin-top:4px}
.snap-legend-swatch{display:inline-block;width:8px;height:8px;border-radius:2px;margin-right:4px;vertical-align:middle}
details.snap-sql{margin-top:6px;font-size:10px}
details.snap-sql pre{white-space:pre-wrap;word-break:break-word;background:var(--bg);border:1px solid var(--border);border-radius:4px;padding:6px;margin:4px 0 0}
footer.snap-footer{margin-top:16px;font-size:10px;color:var(--muted)}
`;

export const SNAPSHOT_SCRIPT_JS = `(function(){
function cellValue(td){
  var raw=td.getAttribute('data-value');
  if(raw===null)raw=td.textContent||'';
  var num=Number(raw);
  return raw!==''&&!isNaN(num)?num:raw.toLowerCase();
}
function sortTable(table,colIdx){
  var tbody=table.tBodies[0];
  if(!tbody)return;
  var dir=(table.getAttribute('data-sort-col')===String(colIdx)&&table.getAttribute('data-sort-dir')==='asc')?'desc':'asc';
  var rows=Array.prototype.slice.call(tbody.rows);
  rows.sort(function(a,b){
    var av=cellValue(a.cells[colIdx]);
    var bv=cellValue(b.cells[colIdx]);
    var cmp=av<bv?-1:av>bv?1:0;
    return dir==='asc'?cmp:-cmp;
  });
  rows.forEach(function(row){tbody.appendChild(row);});
  table.setAttribute('data-sort-col',String(colIdx));
  table.setAttribute('data-sort-dir',dir);
  var head=table.tHead;
  if(head&&head.rows[0]){
    var ths=head.rows[0].cells;
    for(var i=0;i<ths.length;i++)ths[i].classList.remove('snap-sort-asc','snap-sort-desc');
    ths[colIdx].classList.add(dir==='asc'?'snap-sort-asc':'snap-sort-desc');
  }
}
var headers=document.querySelectorAll('table.snap-table[data-sortable] th[data-sort-idx]');
for(var i=0;i<headers.length;i++){
  (function(th){
    th.addEventListener('click',function(){
      var table=th.closest('table');
      if(table)sortTable(table,Number(th.getAttribute('data-sort-idx')));
    });
  })(headers[i]);
}
var themeBtn=document.getElementById('theme-toggle-btn');
if(themeBtn){
  themeBtn.addEventListener('click',function(){
    document.documentElement.classList.toggle('dark');
  });
}
})();
`;

/**
 * Pinned SHA-256 (base64) digests of the two constants above, used as
 * `'sha256-<value>'` CSP source expressions. Recomputed from the live
 * strings by `htmlSnapshotAssets.test.ts` — update alongside any edit to
 * `SNAPSHOT_STYLE_CSS` / `SNAPSHOT_SCRIPT_JS`.
 */
export const SNAPSHOT_STYLE_HASH = '7bBVbOle2WUvuAU5jLOTCKXLif1TViKBVxTO1wixQ/w=';
export const SNAPSHOT_SCRIPT_HASH = 'UjJGFRRW2Pn6F+CeugQ3pYUvo4qOpTrrIh9KzxxMiuo=';
